import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    Select,
} from "@mui/material"
import { Add } from "@mui/icons-material"
import { AdminFilterBar, AdminPageHeader, countLabel } from "../../../components/ui"
import { SimpleSnackbar } from "../../../components/snackbar"
import { handlePromise } from "../../../utils/handlePromisse"
import {
    AIExperiment,
    completeExperiment,
    createExperiment,
    CreateExperimentInput,
    crossoverExperiment,
    ExperimentStatus,
    listExperiments,
    startExperiment,
} from "../../../services/ai-experiment.service"
import { AIExperimentTable } from "./table/ai-experiment-table"
import { AIExperimentFormDialog } from "./form/ai-experiment-form.dialog"
import { EnrollDialog } from "./enroll/enroll-dialog"
import { ResultsDialog } from "./results/results-dialog"

type Confirm = {
    title: string
    message: string
    action: () => Promise<void>
}

export const AIExperimentPage: React.FC = () => {
    const [loading, setLoading] = useState(false)
    const [experiments, setExperiments] = useState<AIExperiment[]>([])

    const [search, setSearch] = useState("")
    const [modelFilter, setModelFilter] = useState("all")
    const [statusFilter, setStatusFilter] = useState<'all' | ExperimentStatus>("all")

    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState("")

    const [formOpen, setFormOpen] = useState(false)

    const [enrollOpen, setEnrollOpen] = useState(false)
    const [resultsOpen, setResultsOpen] = useState(false)
    const [activeId, setActiveId] = useState<number | null>(null)

    const [confirm, setConfirm] = useState<Confirm | null>(null)

    const notify = useCallback((message: string) => {
        setSnackbarMessage(message)
        setSnackbarOpen(true)
    }, [])

    const fetchExperiments = useCallback(async () => {
        setLoading(true)
        const [result, error] = await handlePromise(listExperiments())
        setLoading(false)
        if (error) {
            notify(error.response?.data?.message || "Erro ao carregar experimentos")
            return
        }
        setExperiments(result.data)
    }, [notify])

    useEffect(() => {
        fetchExperiments()
    }, [fetchExperiments])

    const handleCreate = useCallback(async (input: CreateExperimentInput) => {
        const [, error] = await handlePromise(createExperiment(input))
        if (error) {
            notify(error.response?.data?.message || "Erro ao criar experimento")
            return
        }
        setFormOpen(false)
        notify("Experimento criado")
        await fetchExperiments()
    }, [fetchExperiments, notify])

    const runLifecycle = useCallback(async (
        promise: Promise<unknown>,
        successMsg: string,
        errorMsg: string,
    ) => {
        const [, error] = await handlePromise(promise)
        if (error) {
            notify((error as any).response?.data?.message || errorMsg)
            return
        }
        notify(successMsg)
        await fetchExperiments()
    }, [fetchExperiments, notify])

    const handleStart = useCallback((exp: AIExperiment) => {
        runLifecycle(startExperiment(exp.id), "Experimento iniciado", "Erro ao iniciar experimento")
    }, [runLifecycle])

    const handleCrossover = useCallback((exp: AIExperiment) => {
        setConfirm({
            title: "Fazer crossover?",
            message: `Os grupos vão trocar de braço (round ${exp.current_round} → ${exp.current_round + 1}). As mensagens a partir de agora contam na nova rodada.`,
            action: () => runLifecycle(crossoverExperiment(exp.id), "Crossover realizado", "Erro ao fazer crossover"),
        })
    }, [runLifecycle])

    const handleComplete = useCallback((exp: AIExperiment) => {
        setConfirm({
            title: "Concluir experimento?",
            message: "O experimento será encerrado e as participantes deixam de ser roteadas pelos braços do teste. Esta ação não pode ser desfeita.",
            action: () => runLifecycle(completeExperiment(exp.id), "Experimento concluído", "Erro ao concluir experimento"),
        })
    }, [runLifecycle])

    const openManage = (exp: AIExperiment) => {
        setActiveId(exp.id)
        setEnrollOpen(true)
    }

    const openResults = (exp: AIExperiment) => {
        setActiveId(exp.id)
        setResultsOpen(true)
    }

    const confirmAndClose = async () => {
        if (!confirm) return
        const action = confirm.action
        setConfirm(null)
        await action()
    }

    // Distinct models for the Modelo select
    const distinctModels = useMemo(() => {
        const models = new Set<string>()
        experiments.forEach((exp) => {
            if (exp.provider_a) models.add(exp.provider_a)
            if (exp.provider_b) models.add(exp.provider_b)
        })
        return Array.from(models).sort()
    }, [experiments])

    // Client-side filtering
    const filteredExperiments = experiments.filter((exp) => {
        const matchesSearch = !search || exp.name.toLowerCase().includes(search.toLowerCase())
        const matchesModel =
            modelFilter === "all" ||
            exp.provider_a === modelFilter ||
            exp.provider_b === modelFilter
        const matchesStatus = statusFilter === "all" || exp.status === statusFilter
        return matchesSearch && matchesModel && matchesStatus
    })

    return (
        <Box sx={{ width: "100%", p: { xs: "18px 16px 24px", md: 4 } }}>
            <SimpleSnackbar
                show={snackbarOpen}
                message={snackbarMessage}
                onDismiss={() => setSnackbarOpen(false)}
            />

            <AdminPageHeader
                title="Experimentos de IA"
                subtitle="Testes A/B da Cléo"
                action={
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setFormOpen(true)}
                        sx={{ height: 44, borderRadius: "12px", fontWeight: 600 }}
                        disabled={loading}
                    >
                        Novo experimento
                    </Button>
                }
            />

            <AdminFilterBar
                search={search}
                onSearch={setSearch}
                placeholder="Buscar por nome..."
                filters={
                    <>
                        <Select
                            size="small"
                            value={modelFilter}
                            onChange={(e) => setModelFilter(e.target.value)}
                            sx={{ height: 40, minWidth: 180, borderRadius: "10px" }}
                        >
                            <MenuItem value="all">Todos os modelos</MenuItem>
                            {distinctModels.map((model) => (
                                <MenuItem key={model} value={model}>{model}</MenuItem>
                            ))}
                        </Select>
                        <Select
                            size="small"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as 'all' | ExperimentStatus)}
                            sx={{ height: 40, minWidth: 160, borderRadius: "10px" }}
                        >
                            <MenuItem value="all">Todos</MenuItem>
                            <MenuItem value="draft">Rascunho</MenuItem>
                            <MenuItem value="running">Em andamento</MenuItem>
                            <MenuItem value="completed">Concluído</MenuItem>
                        </Select>
                    </>
                }
                count={countLabel(filteredExperiments.length, experiments.length)}
            />

            <AIExperimentTable
                experiments={filteredExperiments}
                onManage={openManage}
                onStart={handleStart}
                onCrossover={handleCrossover}
                onComplete={handleComplete}
                onResults={openResults}
                countLabelText={countLabel(filteredExperiments.length, experiments.length)}
            />

            <AIExperimentFormDialog
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSubmit={handleCreate}
            />

            <EnrollDialog
                open={enrollOpen}
                experimentId={activeId}
                onClose={() => setEnrollOpen(false)}
                onError={notify}
                onNotify={notify}
            />

            <ResultsDialog
                open={resultsOpen}
                experimentId={activeId}
                onClose={() => setResultsOpen(false)}
                onError={notify}
            />

            {/* Diálogo de confirmação de ações irreversíveis — lógica intacta */}
            <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
                <DialogTitle>{confirm?.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{confirm?.message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)}>Cancelar</Button>
                    <Button variant="contained" onClick={confirmAndClose}>Confirmar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
